/**
 * @fileoverview GPU Status UI Component
 * @module gpuUI
 * @description Enterprise-grade component for displaying GPU telemetry and status.
 * Features: A11y (ARIA, Keyboard nav), CSS-in-JS injection, XSS protection, Memory safe.
 * 
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 */

import { getGPUInfo } from '../core/gpuDetector.js';

// ============================================================================
// CONSTANTS & DICTIONARIES
// ============================================================================

const DOM_IDS = {
  BADGE: 'gpu-status-badge',
  MODAL: 'gpu-details-modal',
  OVERLAY: 'gpu-details-overlay',
  STYLE_TAG: 'gpu-ui-styles'
};

const I18N = {
  TITLE: '🖥️ GPU Information',
  CLOSE: 'Close dialog',
  YES: '✅ Yes',
  NO: '❌ No',
  LIMITS_TITLE: 'WebGPU Limits'
};

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Formats bytes into a human-readable string.
 * @param {number} bytes 
 * @param {number} [decimals=1]
 * @returns {string}
 */
const formatBytes = (bytes, decimals = 1) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Safely creates a DOM element with attributes.
 * @param {string} tag 
 * @param {Object} [attributes={}] 
 * @returns {HTMLElement}
 */
const createElement = (tag, attributes = {}) => {
  const el = document.createElement(tag);
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === 'className') el.className = value;
    else if (key === 'textContent') el.textContent = value;
    else if (key.startsWith('data-') || key.startsWith('aria-')) el.setAttribute(key, value);
    else el[key] = value;
  });
  return el;
};

// ============================================================================
// CORE CLASS
// ============================================================================

class GPUUIManager {
  constructor() {
    this._badgeElement = null;
    this._modalElement = null;
    this._overlayElement = null;
    this._boundHandleKeyDown = this._handleKeyDown.bind(this);
    
    this._injectStyles();
  }

  /**
   * Injects component styles once to avoid inline CSS overhead.
   * @private
   */
  _injectStyles() {
    if (document.getElementById(DOM_IDS.STYLE_TAG)) return;

    const style = document.createElement('style');
    style.id = DOM_IDS.STYLE_TAG;
    style.textContent = `
      .gpu-badge {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 6px 12px; border-radius: 8px;
        background: rgba(0,0,0,0.6); backdrop-filter: blur(8px);
        font-family: system-ui, -apple-system, sans-serif; font-size: 12px; color: #ccc;
        border: 1px solid rgba(255,255,255,0.1);
        cursor: pointer; transition: all 0.2s ease;
        user-select: none;
      }
      .gpu-badge:hover, .gpu-badge:focus-visible {
        background: rgba(20,20,20,0.8); border-color: rgba(255,255,255,0.3);
        outline: none; transform: translateY(-1px);
      }
      .gpu-dot {
        width: 8px; height: 8px; border-radius: 50%;
        transition: background 0.3s, box-shadow 0.3s;
      }
      .gpu-tag {
        padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;
        letter-spacing: 0.5px; text-transform: uppercase;
      }
      .gpu-tag.success { background: rgba(34,197,94,0.2); color: #22c55e; }
      .gpu-tag.error { background: rgba(239,68,68,0.2); color: #ef4444; }
      
      .gpu-overlay {
        position: fixed; inset: 0; z-index: 9999;
        background: rgba(0,0,0,0.6); backdrop-filter: blur(2px);
        animation: fadeIn 0.2s ease;
      }
      .gpu-modal {
        position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
        z-index: 10000; background: rgba(15,15,25,0.95);
        border: 1px solid rgba(255,255,255,0.15); border-radius: 12px;
        padding: 24px; min-width: 360px; max-width: 500px; width: 100%;
        color: #e0e0e0; font-family: system-ui, sans-serif;
        box-shadow: 0 24px 48px rgba(0,0,0,0.5);
        animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .gpu-modal-header {
        display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;
      }
      .gpu-modal-close {
        background: none; border: none; color: #888; font-size: 20px;
        cursor: pointer; padding: 4px; border-radius: 4px; transition: color 0.2s;
      }
      .gpu-modal-close:hover { color: #fff; background: rgba(255,255,255,0.1); }
      .gpu-table { width: 100%; font-size: 13px; border-collapse: collapse; }
      .gpu-table td { padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
      .gpu-table td:first-child { color: #888; width: 40%; }
      .gpu-limits { margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.1); }
      
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes slideUp { from { opacity: 0; transform: translate(-50%, -45%); } to { opacity: 1; transform: translate(-50%, -50%); } }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initializes and renders the GPU badge in the specified container.
   * @param {string} containerId 
   * @returns {HTMLElement|null}
   */
  createBadge(containerId) {
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`[GPUUI] Container #${containerId} not found.`);
      return null;
    }

    const info = getGPUInfo();
    if (!info) return null;

    // Destroy existing before creating new
    if (this._badgeElement) this._badgeElement.remove();

    this._badgeElement = createElement('div', {
      id: DOM_IDS.BADGE,
      className: 'gpu-badge',
      role: 'button',
      tabIndex: 0,
      'aria-label': 'Show GPU Status Details'
    });

    const dot = createElement('span', { className: 'gpu-dot' });
    dot.style.background = info.tier.color;
    dot.style.boxShadow = `0 0 6px ${info.tier.color}`;

    const label = createElement('span', { textContent: `GPU: ${info.tier.label}` });
    
    const webgpuTag = createElement('span', {
      className: `gpu-tag ${info.webgpu ? 'success' : 'error'}`,
      textContent: info.webgpu ? 'WebGPU ✓' : 'WebGL2'
    });

    this._badgeElement.append(dot, label, webgpuTag);
    container.appendChild(this._badgeElement);

    // Event Listeners (Click + Keyboard A11y)
    this._badgeElement.addEventListener('click', () => this.showDetails());
    this._badgeElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.showDetails();
      }
    });

    return this._badgeElement;
  }

  /**
   * Constructs and displays the detailed GPU info modal.
   */
  showDetails() {
    const info = getGPUInfo();
    if (!info || this._modalElement) return;

    // Document Fragments for performance (batch DOM insertion)
    const fragment = document.createDocumentFragment();

    // Overlay
    this._overlayElement = createElement('div', { 
      id: DOM_IDS.OVERLAY, 
      className: 'gpu-overlay',
      'aria-hidden': 'true'
    });
    this._overlayElement.addEventListener('click', () => this.closeDetails());

    // Modal
    this._modalElement = createElement('div', {
      id: DOM_IDS.MODAL,
      className: 'gpu-modal',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'gpu-modal-title'
    });

    // Safe DOM construction (No innerHTML for user/system dynamic data)
    this._modalElement.innerHTML = `
      <div class="gpu-modal-header">
        <h3 id="gpu-modal-title" style="margin:0; font-size:16px;">${I18N.TITLE}</h3>
        <button class="gpu-modal-close" aria-label="${I18N.CLOSE}">✕</button>
      </div>
      <table class="gpu-table">
        <tbody id="gpu-table-body"></tbody>
      </table>
    `;

    const tbody = this._modalElement.querySelector('#gpu-table-body');
    const rowsData = [
      ['Tier', info.tier.label, { color: info.tier.color, fontWeight: '600' }],
      ['Renderer', info.renderer],
      ['Vendor', info.vendor],
      ['WebGL2', info.webgl2 ? I18N.YES : I18N.NO],
      ['WebGPU', info.webgpu ? I18N.YES : I18N.NO],
      ['Max Texture', `${info.maxTextureSize}px`],
      ['SharedArrayBuffer', info.sharedArrayBuffer ? I18N.YES : I18N.NO],
      ['Cache Size', `${info.tier.cacheSize} MB`],
      ['Features', info.tier.features.join(', '), { fontSize: '11px' }]
    ];

    rowsData.forEach(([label, value, styles]) => {
      const tr = document.createElement('tr');
      const tdLabel = createElement('td', { textContent: label });
      const tdValue = createElement('td', { textContent: value });
      if (styles) Object.assign(tdValue.style, styles);
      tr.append(tdLabel, tdValue);
      tbody.appendChild(tr);
    });

    // WebGPU Limits Section
    if (info.webgpu && info.webgpuLimits) {
      const limitsDiv = createElement('div', { className: 'gpu-limits' });
      limitsDiv.innerHTML = `
        <div style="font-size:12px; color:#888; margin-bottom:8px;">${I18N.LIMITS_TITLE}</div>
        <div style="font-size:11px; color:#aaa; font-family: monospace;">
          Buffer: ${formatBytes(info.webgpuLimits.maxBufferSize)} |
          Storage: ${formatBytes(info.webgpuLimits.maxStorageBufferBindingSize)} |
          Workgroup: ${info.webgpuLimits.maxComputeWorkgroupSizeX}
        </div>
      `;
      this._modalElement.appendChild(limitsDiv);
    }

    // Bind Close Events
    this._modalElement.querySelector('.gpu-modal-close').addEventListener('click', () => this.closeDetails());
    document.addEventListener('keydown', this._boundHandleKeyDown);

    fragment.append(this._overlayElement, this._modalElement);
    document.body.appendChild(fragment);

    // Focus management for A11y
    this._modalElement.querySelector('.gpu-modal-close').focus();
  }

  /**
   * Closes the modal and cleans up DOM/Events.
   */
  closeDetails() {
    document.removeEventListener('keydown', this._boundHandleKeyDown);
    
    this._overlayElement?.remove();
    this._modalElement?.remove();
    
    this._overlayElement = null;
    this._modalElement = null;

    // Return focus to badge
    this._badgeElement?.focus();
  }

  /**
   * Handles keyboard events for the modal (e.g., Escape).
   * @private
   */
  _handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.closeDetails();
    }
  }

  /**
   * Updates the visual indicator status on the badge.
   * @param {Object} status - { color: string }
   */
  updateStatus(status) {
    if (!this._badgeElement || !status?.color) return;
    
    const dot = this._badgeElement.querySelector('.gpu-dot');
    if (dot) {
      // Use requestAnimationFrame for smooth repaints
      requestAnimationFrame(() => {
        dot.style.background = status.color;
        dot.style.boxShadow = `0 0 6px ${status.color}`;
      });
    }
  }

  /**
   * Destroys the UI component and prevents memory leaks.
   */
  destroy() {
    this.closeDetails();
    this._badgeElement?.remove();
    this._badgeElement = null;
    
    const styleTag = document.getElementById(DOM_IDS.STYLE_TAG);
    styleTag?.remove();
  }
}

// ============================================================================
// EXPORTS (Facade Pattern for Backward Compatibility)
// ============================================================================

const gpuUI = new GPUUIManager();

export const createGPUStatusBadge = (containerId) => gpuUI.createBadge(containerId);
export const showGPUDetails = () => gpuUI.showDetails();
export const updateGPUStatus = (status) => gpuUI.updateStatus(status);
export default gpuUI; // Export instance for advanced usage (e.g., .destroy())
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Real functionality
// ══════════════════════════════════════════════════════════════

let _fpsOverlayEl = null;
let _fpsInterval = null;
let _fpsFrames = [];
let _benchmarkResult = null;

/**
 * Show real-time FPS overlay on viewport
 * Updates every 500ms, shows avg/min/max
 */
export function showFPSOverlay() {
  if (_fpsOverlayEl) return; // already visible
  _fpsOverlayEl = createElement('div', { id: 'gpu-fps-overlay' });
  Object.assign(_fpsOverlayEl.style, {
    position: 'fixed', top: '60px', right: '12px', zIndex: '99999',
    background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
    padding: '6px 12px', borderRadius: '8px', fontFamily: 'monospace',
    fontSize: '11px', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)',
    pointerEvents: 'none', minWidth: '100px',
  });
  document.body.appendChild(_fpsOverlayEl);

  let lastTime = performance.now();
  let frameCount = 0;

  const measure = () => {
    frameCount++;
    const now = performance.now();
    if (now - lastTime >= 500) {
      const fps = Math.round(frameCount / ((now - lastTime) / 1000));
      _fpsFrames.push(fps);
      if (_fpsFrames.length > 120) _fpsFrames.shift();
      const avg = Math.round(_fpsFrames.reduce((a, b) => a + b, 0) / _fpsFrames.length);
      const min = Math.min(..._fpsFrames.slice(-20));
      const max = Math.max(..._fpsFrames.slice(-20));
      const color = fps >= 50 ? '#22c55e' : fps >= 25 ? '#f59e0b' : '#ef4444';
      if (_fpsOverlayEl) {
        _fpsOverlayEl.style.color = color;
        _fpsOverlayEl.textContent = `FPS: ${fps} (avg:${avg} min:${min} max:${max})`;
      }
      frameCount = 0;
      lastTime = now;
    }
    if (_fpsOverlayEl) requestAnimationFrame(measure);
  };
  requestAnimationFrame(measure);
}

/**
 * Hide FPS overlay
 */
export function hideFPSOverlay() {
  if (_fpsOverlayEl) { _fpsOverlayEl.remove(); _fpsOverlayEl = null; }
  _fpsFrames = [];
}

/**
 * Toggle FPS overlay
 */
export function toggleFPSOverlay() {
  _fpsOverlayEl ? hideFPSOverlay() : showFPSOverlay();
}

/**
 * Run GPU benchmark — measures render performance over 3 seconds
 * @returns {Promise<{avgFPS:number, minFPS:number, maxFPS:number, score:string}>}
 */
export async function runGPUBenchmark() {
  try {
    return new Promise(resolve => {
      const frames = [];
      let lastTime = performance.now();
      let frameCount = 0;
      const duration = 3000; // 3 sec
      const startTime = performance.now();
  
      const tick = () => {
        frameCount++;
        const now = performance.now();
        if (now - lastTime >= 200) {
          frames.push(Math.round(frameCount / ((now - lastTime) / 1000)));
          frameCount = 0;
          lastTime = now;
        }
        if (now - startTime < duration) requestAnimationFrame(tick);
        else {
          const avg = Math.round(frames.reduce((a, b) => a + b, 0) / frames.length);
          const min = Math.min(...frames);
          const max = Math.max(...frames);
          let score = 'Low';
          if (avg >= 55) score = 'Excellent';
          else if (avg >= 40) score = 'Good';
          else if (avg >= 25) score = 'Medium';
          _benchmarkResult = { avgFPS: avg, minFPS: min, maxFPS: max, score, timestamp: Date.now() };
          resolve(_benchmarkResult);
        }
      };
      requestAnimationFrame(tick);
    });
  } catch (e) { console.warn(`[gpuUI] runGPUBenchmark failed:`, e.message); return null; }
}

/**
 * Get last benchmark result
 * @returns {Object|null}
 */
export function getBenchmarkResult() { return _benchmarkResult; }

/**
 * Get real-time memory usage (Chrome only)
 * @returns {Object|null}
 */
export function getGPUMemoryUsage() {
  const mem = performance.memory;
  if (!mem) return null;
  return {
    usedMB: +(mem.usedJSHeapSize / 1048576).toFixed(0),
    totalMB: +(mem.totalJSHeapSize / 1048576).toFixed(0),
    limitMB: +(mem.jsHeapSizeLimit / 1048576).toFixed(0),
    usagePercent: +((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100).toFixed(1),
  };
}

/**
 * Detect if GPU is likely thermal-throttling (FPS drop over time)
 * Requires FPS overlay to have been running for > 30 samples
 * @returns {{throttling:boolean, dropPercent:number}}
 */
export function detectThermalThrottling() {
  if (_fpsFrames.length < 30) return { throttling: false, dropPercent: 0, note: 'Insufficient data (need 30+ FPS samples)' };
  const first10 = _fpsFrames.slice(0, 10);
  const last10 = _fpsFrames.slice(-10);
  const avgFirst = first10.reduce((a, b) => a + b, 0) / first10.length;
  const avgLast = last10.reduce((a, b) => a + b, 0) / last10.length;
  const drop = avgFirst > 0 ? ((avgFirst - avgLast) / avgFirst * 100) : 0;
  return { throttling: drop > 20, dropPercent: +drop.toFixed(1) };
}
